/*
 * This file is part of the Advanced Search module for a web application.
 */

const CONFIGURATION = {
  fieldTypes: [
    // {
    //   "label": "Any field",
    //   "name": "anyField",
    //   "conditions": ["wildcardContains"],
    //   "allowMultiple": true,
    //   "hasAutocomplete": false
    // },
    {
      label: 'Place of publication',
      name: 'placeOfPublication',
      conditions: ['fuzzyContains', 'fuzzyContainsNot'],
      allowMultiple: true,
      hasAutocomplete: true
    }
  ],
  conditionTypes: [
    {
      label: 'is',
      name: 'isLiteral',
      operator: '=',
      translation: '%field%="%query%"',
      construction: 'text:query',
      valueLabel: '%query%'
    }
  ]
}

/**
 * Converts an Advanced Search Query String into a Query State object.
 *
 * @param queryString
 * @returns {{}}
 */
export function queryStringToQueryState (queryString, configuration = CONFIGURATION) {
  const tokens = tokenizeQueryString(queryString)
  const lookupTree = tokensToTreeRepresentation(tokens)
  return parseQueryStateFromLookupTree(lookupTree, configuration)
}

/**
 * Converts a Query State object into an Advanced Search Query String.
 *
 * @param queryState
 * @param configuration
 * @returns {*}
 */
export function queryStateToQueryString (queryState, configuration = CONFIGURATION) {
  const simplifiedQueryState = simplifyQueryState(queryState, configuration)
  return simplifiedQueryState.groups.map(group => {
    return group.rows.map(row => {
      const field = configuration.fieldTypes.find(field => field.name === row.field)
      const condition = configuration.conditionTypes.find(condition => condition.name === row.condition)
      const inner = row.values.map(value => {
        return Object.entries(value.data).reduce((s, [k, v]) => {
          return s.replace(`%${k}%`, v)
        }, condition.translation.replace('%field%', field.name))
      }).join(' OR ')
      return row.values.length > 1 ? `(${inner})` : inner
    }).join(' AND ')
  }).join(' OR ')
}

export function queryStringToUrl (queryString) {
  const urlSearchParams = new URLSearchParams()
  const query = queryString.trim()
  if (query) {
    urlSearchParams.set('advanced-query', query)
  } else {
    urlSearchParams.delete('advanced-query')
  }
  const location = window.location
  const url = new URL(location)
  url.search = urlSearchParams.toString()
  return url.toString()
}

export function queryStateToEnglishString (queryState, configuration = CONFIGURATION) {
  const simplifiedQueryState = simplifyQueryState(queryState, configuration)
  return simplifiedQueryState.groups.map((group, index) => {
    const sentence = group.rows.filter(r => r.values.length > 0).map(row => {
      const field = configuration.fieldTypes.find(field => field.name === row.field)
      const condition = field && configuration.conditionTypes.find(condition => condition.name === row.condition)
      const values = row.values.map(value => {
        return Object.entries(value.data).reduce((s, [k, v]) => {
          return s.replace(`%${k}%`, v)
        }, condition.sentenceValueLabel.replace('%field%', field.label))
      })
      const prefix = values.length > 1 ? 'any of ' : ''
      const valuesString = values.length > 1 ? prefix + values.slice(0, -1).join(', ') + ' or ' + values.slice(-1)[0] : values[0]
      return condition ? `the ${field.label.toLowerCase()} ${condition.label} ${valuesString}` : ''
    }).join(' and ')
    return sentence && index === 0 ? sentence[0].toUpperCase() + sentence.slice(1) : sentence
  }).join('. Alternatively, ') + '.'
}

export function simplifyQueryState (queryState, configuration) {
  const simplifiedQueryState = createQueryState([])
  queryState.groups.forEach(group => {
    const newGroup = createQueryStateGroup(group.rows.filter(row => row.values?.length > 0))

    if (newGroup.rows.length === 0) {
      return
    }

    simplifiedQueryState.groups.push(createQueryStateGroup(group.rows.filter(row => row.values?.length > 0)))
  })
  return simplifiedQueryState
}

/**
 * Adds a new group to the Query State.
 *
 * @param queryState
 * @returns {*}
 */
export function addGroupToQueryState (queryState) {
  queryState.groups.push(createQueryStateGroup())
  return queryState
}

/**
 * Removes a specific group from the Query State.
 *
 * @param queryState
 * @param groupUuid
 * @returns {*}
 */
export function removeGroupFromQueryState (queryState, groupUuid) {
  const groupIndex = queryState.groups.findIndex(group => group.uuid === groupUuid)
  if (groupIndex !== -1) {
    queryState.groups.splice(groupIndex, 1)
  } else {
    console.warn(`Group with UUID ${groupUuid} not found`)
  }
  if (isEmptyQueryState(queryState)) {
    queryState.groups = [createQueryStateGroup()]
  }
  return queryState
}

/**
 * Splits a group into two groups at a specific row.
 *
 * @param queryState
 * @param groupUuid
 * @param rowUuid
 */
export function splitQueryStateGroup (queryState, groupUuid, rowUuid) {
  for (let i = 0; i < queryState.groups.length; i++) {
    const group = queryState.groups[i]
    if (group.uuid === groupUuid) {
      const rowIndex = group.rows.findIndex(row => row.uuid === rowUuid)
      if (rowIndex > 0) {
        const newGroup = createQueryStateGroup(group.rows.slice(rowIndex))
        group.rows.length = rowIndex
        queryState.groups.splice(i + 1, 0, newGroup)
      } else if (rowIndex === 0) {
        console.warn(`Row with UUID ${rowUuid} is the first row in group ${groupUuid}, cannot split`)
      } else {
        console.warn(`Row with UUID ${rowUuid} not found in group ${groupUuid}`)
      }
      break
    }
  }
  return queryState
}

/**
 * Combines two groups into one.
 *
 * @param queryState
 * @param groupUuid
 * @returns {*}
 */
export function combineQueryStateGroups (queryState, groupUuid) {
  const groupIndex = queryState.groups.findIndex(group => group.uuid === groupUuid)
  if (groupIndex > 0) {
    const prevGroup = queryState.groups[groupIndex - 1]
    const currentGroup = queryState.groups[groupIndex]
    prevGroup.rows.push(...currentGroup.rows)
    queryState.groups.splice(groupIndex, 1)
  } else {
    console.warn(`Group with UUID ${groupUuid} not found or is the first group`)
  }
  return queryState
}

/**
 * Adds a new row to the Query State.
 *
 * @param queryState
 * @param groupUuid
 * @returns {*}
 */
export function addRowToQueryState (queryState, groupUuid) {
  const group = queryState.groups.find(group => group.uuid === groupUuid)
  if (group) {
    group.rows.push(createQueryStateRow())
  } else {
    console.warn(`Group with UUID ${groupUuid} not found`)
  }
  return queryState
}

/**
 * Removes a specific row from the Query State.
 * @param queryState
 * @param rowUuid
 * @returns {*}
 */
export function removeRowFromQueryState (queryState, rowUuid) {
  const group = queryState.groups.find(group => group.rows.some(row => row.uuid === rowUuid))
  if (group) {
    const rowIndex = group.rows.findIndex(row => row.uuid === rowUuid)
    if (rowIndex !== -1) {
      group.rows.splice(rowIndex, 1)
    } else {
      console.warn(`Row with UUID ${rowUuid} not found in group ${group.uuid}`)
    }
  } else {
    console.warn(`Row with UUID ${rowUuid} not found`)
  }
  // If the group is empty, remove it
  if (group && group.rows.length === 0) {
    const groupIndex = queryState.groups.findIndex(g => g.uuid === group.uuid)
    if (groupIndex !== -1) {
      queryState.groups.splice(groupIndex, 1)
    } else {
      console.warn(`Group with UUID ${group.uuid} not found`)
    }
  }
  if (isEmptyQueryState(queryState)) {
    queryState.groups = [createQueryStateGroup()]
  }
  return queryState
}

/**
 * Alters the field type of a specific row in the Query State.
 * @param queryState
 * @param rowUuid
 * @param newFieldType
 * @param configuration
 * @returns {*}
 */
export function alterRowFieldType (queryState, rowUuid, newFieldType, configuration = CONFIGURATION) {
  const row = queryState.groups.flatMap(group => group.rows).find(row => row.uuid === rowUuid)
  const fieldType = configuration.fieldTypes.find(field => field.name === newFieldType)
  if (row && fieldType) {
    row.field = newFieldType
    row.condition = fieldType.conditions[0]
    row.values = []
  } else {
    console.warn(`Field type ${newFieldType} not found`)
  }
  return queryState
}

/**
 * Alters the condition type of a specific row in the Query State.
 *
 * @param queryState
 * @param rowUuid
 * @param newConditionType
 * @param configuration
 * @returns {*}
 */
export function alterRowConditionType (queryState, rowUuid, newConditionType, configuration = CONFIGURATION) {
  const row = queryState.groups.flatMap(group => group.rows).find(row => row.uuid === rowUuid)
  const conditionType = configuration.conditionTypes.find(condition => condition.name === newConditionType)
  if (row && conditionType) {
    const fieldType = configuration.fieldTypes.find(field => field.name === row.field)
    if (fieldType && fieldType.conditions.includes(newConditionType)) {
      row.condition = newConditionType
      row.values = []
    } else {
      console.warn(`Condition type ${newConditionType} is not compatible with field type ${row.field}`)
    }
  } else {
    console.warn(`Condition type ${newConditionType} not found`)
  }
  return queryState
}

/**
 * Adds a new value to a specific row in the Query State.
 *
 * @param queryState
 * @param rowUuid
 * @param newValue the data for the new value
 * @returns {*}
 */
export function addRowValue (queryState, rowUuid, newValue) {
  const row = queryState.groups.flatMap(group => group.rows).find(row => row.uuid === rowUuid)
  if (row) {
    row.values.push(createQueryStateValue(newValue))
  } else {
    console.warn(`Row with UUID ${rowUuid} not found`)
  }
  return queryState
}

/**
 * Removes a specific value from a specific row in the Query State.
 *
 * @param queryState
 * @param rowUuid
 * @param valueUuid
 * @returns {*}
 */
export function removeRowValue (queryState, rowUuid, valueUuid) {
  const row = queryState.groups.flatMap(group => group.rows).find(row => row.uuid === rowUuid)
  if (row) {
    const valueIndex = row.values.findIndex(value => value.uuid === valueUuid)
    if (valueIndex !== -1) {
      row.values.splice(valueIndex, 1)
    } else {
      console.warn(`Value with UUID ${valueUuid} not found in row ${row.uuid}`)
    }
  } else {
    console.warn(`Row with UUID ${rowUuid} not found`)
  }
  return queryState
}

/**
 * Alters the data of a specific value of a specific row in the Query State.
 * @param queryState
 * @param rowUuid
 * @param valueUuid
 * @param newValue
 * @returns {*}
 */
export function alterRowValue (queryState, rowUuid, valueUuid, newValue) {
  const row = queryState.groups.flatMap(group => group.rows).find(row => row.uuid === rowUuid)
  if (row) {
    const value = row.values.find(value => value.uuid === valueUuid)
    if (value) {
      value.data = newValue
    } else {
      console.warn(`Value with UUID ${valueUuid} not found in row ${row.uuid}`)
    }
  } else {
    console.warn(`Row with UUID ${rowUuid} not found`)
  }
  return queryState
}

/**
 * Parses a build instruction for a specific condition type.
 *
 * The returned value is an array for 2-value tuples.
 * The first value is the type of the value, and the second value is the name of the value.
 *
 * @param conditionName
 * @param configuration
 * @returns {string[][]|null}
 */
export function parseBuildInstruction (conditionName, configuration = CONFIGURATION) {
  const condition = configuration.conditionTypes.find(condition => condition.name === conditionName)
  if (condition) {
    return condition.construction.split(';').map(v => v.split(':'))
  } else {
    console.warn(`Condition type ${conditionName} not found`)
    return null
  }
}

/**
 * Pretty prints a Query State object to the console.
 *
 * @param queryState
 * @param configuration
 */
export function prettyPrintQueryState (queryState, configuration) {
  // Pretty print
  queryState.groups.forEach((group, i) => {
    if (i > 0) {
      console.log('OR')
    }
    group.rows.forEach((row, i) => {
      if (i > 0) {
        console.log('AND')
      }
      const field = configuration.fieldTypes.find(field => field.name === row.field)
      const condition = configuration.conditionTypes.find(condition => condition.name === row.condition)
      console.log(field.label, condition.label, row.values.map(value => Object.entries(value).reduce((s, [key, value]) => {
        return s.replace(`%${key}%`, value)
      }, condition.valueLabel)).join(', '))
    })
  })
}

export function isEmptyQueryState (queryState) {
  return queryState.groups.length === 0 || queryState.groups.every(group => group.rows.length === 0)
}

export function createQueryState (groups = null) {
  return {
    uuid: crypto.randomUUID(),
    groups: groups !== null ? groups : [
      createQueryStateGroup([
        createQueryStateRow(),
        createQueryStateRow(null, 'AND')
      ])
    ]
  }
}

function createQueryStateGroup (rows = null) {
  return {
    uuid: crypto.randomUUID(),
    rows: rows !== null ? rows : [createQueryStateRow()]
  }
}

function createQueryStateRow (field = null, condition = null, values = null) {
  return {
    uuid: crypto.randomUUID(),
    field,
    condition,
    values: values !== null ? values : []
  }
}

// TODO: This does not need a UUID, there is no need for multiple values with the same data
function createQueryStateValue (data = null) {
  return {
    uuid: crypto.randomUUID(),
    data: data || {}
  }
}

function tokenizeQueryString (queryString) {
  const tokens = []
  let token = ''
  let inQuotes = false
  let pointer = 0
  let prevChar = ''
  const valueChars = /[a-zA-Z0-9_]/ // Alphanumeric characters
  const whitespaceChars = /\s/ // Whitespace characters
  const boundaryChars = /[*+()]/ // Boundary characters
  while (pointer < queryString.length) {
    const char = queryString[pointer]
    if (char === '"') {
      inQuotes = !inQuotes
      if (!inQuotes) {
        tokens.push(token)
        token = ''
      } else if (token) {
        tokens.push(token)
        token = ''
      }
    } else if (!inQuotes && (char.match(whitespaceChars) || char.match(boundaryChars) || prevChar.match(boundaryChars) || (!!char.match(valueChars) !== !!prevChar.match(valueChars)))) {
      if (token) {
        tokens.push(token)
      }
      token = char.match(whitespaceChars) ? '' : char
    } else {
      token += char
    }
    prevChar = char
    pointer++
  }
  if (token) {
    tokens.push(token)
  }
  return tokens
}

function tokensToTreeRepresentation (tokens) {
  const tree = []
  const stack = []
  let pointer = 0
  let mutableTokens = [...tokens]
  while (pointer < mutableTokens.length) {
    const token = mutableTokens[pointer]
    if (token === '(') {
      if (stack.length === 0) {
        const subQuery = mutableTokens.slice(0, pointer)
        if (subQuery) {
          tree.push(...subQuery)
        }
        mutableTokens = mutableTokens.slice(pointer + 1)
        pointer = 0
      }
      stack.push(pointer)
    } else if (token === ')') {
      stack.pop()
      if (stack.length === 0) {
        const subQuery = mutableTokens.slice(0, pointer)
        const subTree = tokensToTreeRepresentation(subQuery)
        if (subTree.length === 1) {
          tree.push(subTree[0])
        } else {
          tree.push(subTree)
        }
        mutableTokens = mutableTokens.slice(pointer + 1)
        pointer = 0
      }
    }
    pointer++
  }
  // If there are unmatched parentheses, handle them here
  if (stack.length > 0) {
    const unmatchedTree = tokensToTreeRepresentation(mutableTokens)
    if (unmatchedTree.length === 1) {
      tree.push(unmatchedTree[0])
    } else {
      tree.push(unmatchedTree)
    }
  } else if (mutableTokens.length > 0) {
    tree.push(...mutableTokens)
  }

  return tree
}

function parseQueryStateFromLookupTree (lookupTree, configuration, strict = true) {
  function appendBestMatchingPattern () {
    // console.log('We chose pattern:', patterns[longestPatternMatchIndex][0], 'with field', matchingPositions[longestPatternMatchIndex][2])
    const fieldName = matchingPositions[longestPatternMatchIndex][2]
    const condition = patterns[longestPatternMatchIndex][1]
    const patternReverse = tokenizeQueryString(condition.translation.replace(/[%()]/g, ''))
    const data = {}
    for (let i = 0; i < patternReverse.length; i++) {
      const token = patternReverse[i]
      if (token !== 'field' && token.match(/[a-zA-Z0-9]+/)) {
        data[token] = patternMatchStack[i]
      }
    }

    if (currentOperator === 'OR' && currentRow && currentRow.field === fieldName && currentRow.condition === condition.name) {
      currentRow.values.push(createQueryStateValue(data))
    } else {
      if (currentRow) {
        currentGroup.rows.push(currentRow)

        if (currentOperator === 'OR') {
          // This value is the first of a new group
          if (currentGroup) {
            groups.push(currentGroup)
          }
          currentGroup = createQueryStateGroup()
        }
      }

      currentRow = createQueryStateRow(fieldName, condition.name, [createQueryStateValue(data)])
    }

    // Reset the matching position
    for (let i = 0; i < matchingPositions.length; i++) {
      matchingPositions[i][1] = 0
      matchingPositions[i][2] = ''
      matchingPositions[i][3] = true
    }
    // Reset the value stack
    patternMatchStack.length = 0
    longestPatternMatch = 0
    longestPatternMatchIndex = -1
  }

  const groups = []
  const patterns = []
  for (const condition of configuration.conditionTypes) {
    let pattern = tokenizeQueryString(condition.translation.replace(/[%()]/g, ''))
    const types = condition.construction.split(';').reduce((acc, type) => {
      const [typeValue, typeName] = type.split(':')
      acc[typeName] = typeValue
      return acc
    }, {})
    pattern = pattern.map(token => token in types ? types[token] : token)
    patterns.push([pattern, condition])
  }
  // For each pattern, how much does it currently match?
  const matchingPositions = patterns.map((_, i) => {
    return [i, 0, '', true]
  })
  let currentGroup = createQueryStateGroup([])
  let currentRow = null
  let currentOperator = ''
  let longestPatternMatch = 0
  let longestPatternMatchIndex = -1

  const patternMatchStack = []
  const stack = [[lookupTree, 0]]

  while (stack.length) {
    const [node, at] = stack.pop()
    if (at >= node.length) {
      continue
    }
    const value = node[at]

    if (Array.isArray(value)) {
      stack.push([node, at + 1])
      stack.push([value, 0])
    } else {
      stack.push([node, at + 1])

      if (['AND', 'OR', '+', '*'].includes(value.toUpperCase()) && longestPatternMatchIndex !== -1) {
        appendBestMatchingPattern()
        // if (longestPatternMatchIndex !== -1) {
        // }

        if (value === 'AND' || value === '*') {
          if (currentRow) {
            if (currentGroup) {
              currentGroup.rows.push(currentRow)
            } else {
              currentGroup = {
                rows: [currentRow]
              }
            }
            currentRow = null
          }
          currentOperator = 'AND'
        } else if (value === 'OR' || value === '+') {
          // We are not sure yet how to handle this
          currentOperator = 'OR'
        }
        continue
      }

      patternMatchStack.push(value)
      const maybeFieldType = configuration.fieldTypes.find(field => field.name === value)
      // Check if the value matches any of the patterns
      for (let i = 0; i < matchingPositions.length; i++) {
        const [patternIndex, position] = matchingPositions[i]
        const [pattern, condition] = patterns[patternIndex]

        if (!matchingPositions[i][3] || pattern[position] === undefined) {
          continue
        }

        if (pattern[position] === 'field') {
          if (maybeFieldType && maybeFieldType.conditions.includes(condition.name)) {
            matchingPositions[i][1]++
            matchingPositions[i][2] = value
          } else {
            matchingPositions[i][3] = false
          }
        } else if (pattern[position] === 'year') {
          if (value.match(/^\d{4}$/)) {
            matchingPositions[i][1]++
          } else {
            matchingPositions[i][3] = false
          }
        } else if (pattern[position] === 'text') {
          matchingPositions[i][1]++
        } else {
          // Check if the value matches the pattern
          if (value.toUpperCase() === pattern[position].toUpperCase()) {
            matchingPositions[i][1]++
          } else {
            matchingPositions[i][3] = false
          }
        }

        if (matchingPositions[i][1] === pattern.length) {
          if (pattern.length > longestPatternMatch) {
            longestPatternMatch = pattern.length
            longestPatternMatchIndex = i
          }
        }
      }
    }
  }

  if (longestPatternMatchIndex !== -1) {
    appendBestMatchingPattern()
  }

  if (currentRow) {
    currentGroup.rows.push(currentRow)
  }

  if (currentGroup.rows.length > 0) {
    groups.push(currentGroup)
  }

  return createQueryState(groups)
}
